import { AlertTriangle } from 'lucide-react';
import * as React from 'react';
import { toast } from 'sonner';

import {
  Alert,
  AlertDescription,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/cargofl-ui';
import { ApiError } from '@/lib/api';

/**
 * Confirm before something significant happens.
 *
 * For actions that are not deletions but still should not happen on a stray click -
 * changing someone's role, reinstating an exited employee. Both of those revoke every
 * session that person has, and both used to fire straight from a dropdown or a button
 * with nothing in between.
 *
 * Deliberately lighter than BulkDeleteDialog: no typed confirmation. That belongs on
 * the one irreversible action that operates on many rows at once. Demanding it
 * everywhere trains people to type DELETE without reading, which is worse than not
 * asking at all.
 *
 * The button carries the VERB, not "OK" - a dialog whose confirm button says the thing
 * it will do can be answered without re-reading the title.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  /** The one thing they should know before saying yes. Optional. */
  consequence,
  confirmLabel,
  destructive = false,
  onConfirm,
  successMessage,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: React.ReactNode;
  consequence?: React.ReactNode;
  confirmLabel: string;
  destructive?: boolean;
  onConfirm: () => Promise<unknown>;
  successMessage?: string;
}) {
  const [busy, setBusy] = React.useState(false);

  const run = async () => {
    setBusy(true);
    try {
      await onConfirm();
      if (successMessage) toast.success(successMessage);
      onOpenChange(false);
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        {consequence && (
          <Alert className={destructive ? 'border-destructive/30' : 'border-warning/40 bg-warning/5'}>
            <AlertDescription className="flex gap-2 text-xs leading-relaxed">
              <AlertTriangle
                className={`mt-0.5 size-3.5 shrink-0 ${destructive ? 'text-destructive' : 'text-warning'}`}
              />
              <span>{consequence}</span>
            </AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant={destructive ? 'destructive' : 'default'}
            disabled={busy}
            onClick={() => void run()}
          >
            {busy ? 'Working…' : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
